import { z } from 'zod'
import { roleSchema } from './roles.ts'

/** A person as the API returns them (never includes Google ids or session data). */
export const publicUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  role: roleSchema,
  isHrLead: z.boolean(),
  dept: z.string().nullable(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type PublicUser = z.infer<typeof publicUserSchema>

export const meResponseSchema = z.object({ user: publicUserSchema })
export type MeResponse = z.infer<typeof meResponseSchema>

export const usersListResponseSchema = z.object({ users: z.array(publicUserSchema) })
export type UsersListResponse = z.infer<typeof usersListResponseSchema>

export const authConfigSchema = z.object({
  googleEnabled: z.boolean(),
  devLogin: z.boolean(),
  allowedDomain: z.string().nullable(),
})
export type AuthConfigResponse = z.infer<typeof authConfigSchema>

/** `?error=` codes the server sends to `/login`. */
export const LOGIN_ERRORS = [
  'domain_not_allowed',
  'archived',
  'oauth_failed',
  'google_disabled',
] as const
export type LoginError = (typeof LOGIN_ERRORS)[number]

export const devLoginSchema = z.object({
  email: z.email().max(255),
  name: z.string().trim().min(1).max(120).optional(),
  role: roleSchema.optional(),
  isHrLead: z.boolean().optional(),
})
export type DevLoginBody = z.infer<typeof devLoginSchema>

export const userPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    role: roleSchema.optional(),
    isHrLead: z.boolean().optional(),
    dept: z.string().trim().max(120).nullable().optional(),
    archived: z.boolean().optional(),
  })
  .strict()
export type UserPatch = z.infer<typeof userPatchSchema>

/** `POST /api/users`: pre-create a person before their first Google sign-in (linked by email). */
export const userCreateSchema = z
  .object({
    email: z.email().max(255),
    name: z.string().trim().min(1).max(120),
    dept: z.string().trim().max(120).nullable().optional(),
    role: roleSchema.optional(),
    isHrLead: z.boolean().optional(),
  })
  .strict()
export type UserCreate = z.infer<typeof userCreateSchema>

/**
 * `POST /api/users/batch`: Settings → People save. All rows are checked against the final state
 * (e.g. at least one active HR team member) and applied together, or nothing is.
 */
export const userBatchSchema = z
  .object({
    updates: z
      .array(
        userPatchSchema
          .extend({
            id: z.string().min(1).max(64),
            /**
             * The person's `updatedAt` the edit started from; a different stored value means
             * someone else changed them meanwhile (409 `users_conflict`). Optional for scripts.
             */
            updatedAt: z.iso.datetime().optional(),
          })
          .strict(),
      )
      .max(500)
      .default([]),
    creates: z.array(userCreateSchema).max(100).default([]),
  })
  .strict()
export type UserBatch = z.infer<typeof userBatchSchema>
export const USERS_CONFLICT_MESSAGE =
  'Someone else changed people in Settings. Reload to see their changes.'
export type UserBatchInput = z.input<typeof userBatchSchema>

export interface DemoPerson {
  /** Local part of the email; the domain comes from ALLOWED_GOOGLE_DOMAIN. */
  handle: string
  name: string
  dept: string
  role: 'viewer' | 'member' | 'admin'
  isHrLead: boolean
}

/**
 * Demo people from the PRD appendix, seeded in development so the dev sign-in personas map to
 * real rows. The viewer is not in the PRD appendix; it exists to exercise the view-only role.
 */
export const DEMO_PEOPLE: readonly DemoPerson[] = [
  { handle: 'chetana', name: 'Chetana', dept: 'HR team', role: 'admin', isHrLead: true },
  {
    handle: 'divya.krishnan',
    name: 'Divya Krishnan',
    dept: 'HR business partner',
    role: 'admin',
    isHrLead: false,
  },
  { handle: 'rohan.das', name: 'Rohan Das', dept: 'HR operations', role: 'admin', isHrLead: false },
  {
    handle: 'priya.sharma',
    name: 'Priya Sharma',
    dept: 'Engineering',
    role: 'member',
    isHrLead: false,
  },
  {
    handle: 'karthik.rao',
    name: 'Karthik Rao',
    dept: 'Engineering',
    role: 'member',
    isHrLead: false,
  },
  { handle: 'rahul.verma', name: 'Rahul Verma', dept: 'Sales', role: 'member', isHrLead: false },
  { handle: 'ananya.iyer', name: 'Ananya Iyer', dept: 'Content', role: 'member', isHrLead: false },
  {
    handle: 'sneha.patel',
    name: 'Sneha Patel',
    dept: 'Customer success',
    role: 'member',
    isHrLead: false,
  },
  {
    handle: 'vikram.singh',
    name: 'Vikram Singh',
    dept: 'Marketing',
    role: 'member',
    isHrLead: false,
  },
  {
    handle: 'meera.nair',
    name: 'Meera Nair',
    dept: 'Teacher operations',
    role: 'member',
    isHrLead: false,
  },
  {
    handle: 'neha.kapoor',
    name: 'Neha Kapoor',
    dept: 'Finance (view only)',
    role: 'viewer',
    isHrLead: false,
  },
]

/** Dev sign-in quick buttons (DESIGN.md persona chooser stand-in). */
export const DEV_PERSONAS = [
  { label: 'HR lead (admin)', handle: 'chetana' },
  { label: 'HR team (admin)', handle: 'divya.krishnan' },
  { label: 'Employee (member)', handle: 'priya.sharma' },
  { label: 'Viewer', handle: 'neha.kapoor' },
] as const

export const DEFAULT_DEMO_DOMAIN = 'myyogateacher.com'

export function demoEmail(handle: string, domain: string | null | undefined): string {
  return `${handle}@${domain || DEFAULT_DEMO_DOMAIN}`
}

/** True when `email` belongs to `domain` (case-insensitive). No domain means any. */
export function emailInDomain(email: string, domain: string | null | undefined): boolean {
  if (!domain) return true
  return email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)
}
